
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { useCreateRoyaltyReport } from '@/hooks/useRoyaltyReports';
import { useAuthors } from '@/hooks/useAuthors';
import { useBooks } from '@/hooks/useBooks';
import { DialogFooter } from '@/components/ui/dialog';
import FormHeader from './CreateReportForm/FormHeader';
import BookSelection from './CreateReportForm/BookSelection';
import StatusSelection from './CreateReportForm/StatusSelection';
import { useBookFormLogic } from './CreateReportForm/useBookFormLogic';
import { EnhancedBookData } from './CreateReportForm/EnhancedBookSelectionCard';

const currentYear = new Date().getFullYear();

const enhancedBookReportSchema = z.object({
  bookId: z.string().uuid(),
  unitsSold: z.coerce.number().int().min(0),
  grossSales: z.coerce.number().min(0),
  netSales: z.coerce.number().min(0),
  royaltyAmount: z.coerce.number().min(0),
  bookFormat: z.enum(['hardcover', 'paperback', 'ebook', 'audiobook']),
  country: z.string().min(2),
  formatId: z.string(),
});

const royaltyReportSchema = z.object({
  authorId: z.string().uuid({ message: 'Please select an author.' }),
  year: z.coerce.number().int().min(2000).max(currentYear),
  quarter: z.coerce.number().int().min(1).max(4),
  status: z.enum(['draft', 'published']),
  books: z.array(enhancedBookReportSchema).min(1, { message: 'Please select at least one book format.' }),
});

type FormData = z.infer<typeof royaltyReportSchema>;

type SimpleAuthor = {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'author';
  avatar_url?: string;
  created_at: string;
  updated_at: string;
};

interface CreateReportFormProps {
  onSuccess?: () => void;
  authorId?: string;
}

export default function CreateReportForm({ onSuccess, authorId }: CreateReportFormProps) {
  const { toast } = useToast();
  const createReport = useCreateRoyaltyReport();
  const { data: authors, isLoading: isLoadingAuthors } = useAuthors();
  const { data: books, isLoading: isLoadingBooks } = useBooks();
  
  const form = useForm<FormData>({
    resolver: zodResolver(royaltyReportSchema),
    defaultValues: {
      authorId: authorId || '',
      year: currentYear,
      quarter: (Math.floor((new Date().getMonth() / 3)) + 1) as 1 | 2 | 3 | 4,
      status: 'draft',
      books: [],
    },
  });

  const {
    bookFormatGroups,
    toggleBook,
    addBookFormat,
    removeBookFormat,
    updateBookData
  } = useBookFormLogic(form);

  // Set authorId when provided as prop
  useEffect(() => {
    if (authorId) {
      form.setValue('authorId', authorId);
    }
  }, [authorId, form]);

  // Filter books based on selected author
  const selectedAuthorId = form.watch('authorId');
  const authorBooks = books?.filter(book => book.authorId === selectedAuthorId) || [];
  
  // Reset book selection when author changes
  useEffect(() => {
    if (selectedAuthorId) {
      form.setValue('books', []);
    }
  }, [selectedAuthorId, form]);

  const onSubmit = async (data: FormData) => {
    try {
      // Create individual reports for each book format
      for (const bookData of data.books) {
        const reportData = {
          authorId: data.authorId,
          bookId: bookData.bookId,
          year: data.year,
          quarter: data.quarter,
          country: bookData.country,
          salesCount: bookData.unitsSold,
          amount: bookData.royaltyAmount,
          status: data.status,
          currency: 'USD',
          unitsSold: bookData.unitsSold,
          grossSales: bookData.grossSales,
          netSales: bookData.netSales,
          royaltyAmount: bookData.royaltyAmount,
          bookFormat: bookData.bookFormat,
        };
        
        await createReport.mutateAsync(reportData);
      }
      
      form.reset();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error creating royalty reports:', error);
    }
  };

  return (
    <div className="w-full max-w-none">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormHeader
            form={form}
            authors={authors as SimpleAuthor[]}
            isLoadingAuthors={isLoadingAuthors}
            authorId={authorId}
          />
          
          {selectedAuthorId && authorBooks.length > 0 && (
            <BookSelection
              form={form}
              authorBooks={authorBooks}
              bookFormatGroups={bookFormatGroups}
              onToggleBook={toggleBook}
              onUpdateBookData={updateBookData}
              onAddFormat={addBookFormat}
              onRemoveFormat={removeBookFormat}
            />
          )}
          
          <StatusSelection form={form} />
          
          <DialogFooter className="pt-4 sticky bottom-0 bg-white border-t mt-6">
            <Button 
              type="submit" 
              disabled={createReport.isPending}
              className="bg-royalty-600 hover:bg-royalty-700 w-full sm:w-auto"
            >
              {createReport.isPending ? 'Creating...' : 'Create Reports'}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
